<template>
  <div class="auth-wrapper auth-v1 px-2">
    <div class="auth-inner py-2">
      <b-card class="mb-0">
        <!-- Logo e Cabeçalho -->
        <b-link class="brand-logo m-0">
          <img class="logo-dkp" src="logo.png" alt="logo TrendECommerce">
        </b-link>

        <b-card-title class="mb-1">Sua oportunidade começa aqui 🚀</b-card-title>
        <b-card-text class="mb-2">
          Complete seu cadastro em algumas etapas simples!
        </b-card-text>

        <!-- Stepper Header -->
        <div class="bs-stepper-header mb-4">
          <div
            v-for="(step, index) in steps"
            :key="index"
            class="step"
            :class="{ 'active': currentStep === index + 1, 'completed': currentStep > index + 1 }"
          >
            <button class="step-trigger">
              <span class="bs-stepper-circle">
                <feather-icon
                  v-if="currentStep > index + 1"
                  icon="CheckIcon"
                  size="16"
                />
                <span v-else>{{ index + 1 }}</span>
              </span>
              <span class="bs-stepper-label">{{ step.title }}</span>
            </button>
            <div v-if="index < steps.length - 1" class="line"></div>
          </div>
        </div>

        <!-- Formulários -->
        <validation-observer ref="registerForm">
          <!-- Etapa 1: Dados de Acesso -->
          <div v-show="currentStep === 1">
            <b-form-group label="Email" label-for="email">
              <validation-provider
                #default="{ errors }"
                name="Email"
                rules="required|email"
              >
                <b-form-input
                  id="email"
                  v-model="formData.login"
                  :state="errors.length > 0 ? false:null"
                  placeholder="email@exemplo.com"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <b-form-group label="Senha" label-for="password">
              <validation-provider
                #default="{ errors }"
                name="Senha"
                rules="required|min:6"
              >
                <b-input-group class="input-group-merge">
                  <b-form-input
                    id="password"
                    v-model="formData.senha"
                    :type="passwordFieldType"
                    :state="errors.length > 0 ? false:null"
                    placeholder="········"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      :icon="passwordToggleIcon"
                      class="cursor-pointer"
                      @click="togglePasswordVisibility"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <b-form-group label="Tipo de Conta" label-for="group">
              <validation-provider
                #default="{ errors }"
                name="Grupo"
                rules="required"
              >
                <b-form-select
                  id="group"
                  v-model="formData.id_grupousuario"
                  :options="groupOptions"
                  :state="errors.length > 0 ? false:null"
                  :disabled="isGroupReadOnly"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <b-form-group label="Código de Convite" label-for="code">
              <validation-provider
                #default="{ errors }"
                name="Código de Convite"
              >
                <b-form-input
                  id="code"
                  v-model="formData.codigo"
                  :state="errors.length > 0 ? false : null"
                  placeholder="Digite o código de convite (opcional)"
                  maxlength="5"
                  class="small-input"
                  :readonly="isCodeReadOnly"
                />
                <small class="text-muted">Este campo é opcional. Preencha apenas se você recebeu um código de convite.</small>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </div>

          <!-- Etapa 2: Dados da Empresa/Pessoa -->
          <div v-show="currentStep === 2">
            <b-form-group label="Tipo de Pessoa" label-for="tipo_pessoa">
              <b-form-radio-group
                v-model="formData.tipo_pessoa"
                :options="[
                  { text: 'Pessoa Física', value: 'F' },
                  { text: 'Pessoa Jurídica', value: 'J' }
                ]"
              />
            </b-form-group>

            <template v-if="formData.tipo_pessoa === 'J'">
              <b-form-group label="CNPJ" label-for="cnpj">
                <validation-provider
                  #default="{ errors }"
                  name="CNPJ"
                  rules="required"
                >
                  <b-form-input
                    id="cnpj"
                    v-model="formData.cnpj"
                    v-mask="'##.###.###/####-##'"
                    placeholder="00.000.000/0000-00"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="Razão Social" label-for="razao_social">
                <validation-provider
                  #default="{ errors }"
                  name="Razão Social"
                  rules="required"
                >
                  <b-form-input
                    id="razao_social"
                    v-model="formData.razao_social"
                    placeholder="Razão Social da Empresa"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group>
                <b-form-checkbox
                  id="contribuinte_ie"
                  v-model="formData.contribuinte_ie"
                  name="contribuinte_ie"
                  class="custom-control-primary"
                >
                  Contribuinte de ICMS
                </b-form-checkbox>
              </b-form-group>

              <b-form-group label="Inscrição Estadual" label-for="inscricao_estadual">
                <b-form-input
                  id="inscricao_estadual"
                  v-model="formData.inscricao_estadual"
                  placeholder="Inscrição Estadual"
                  :disabled="!formData.contribuinte_ie"
                />
              </b-form-group>

              <b-form-group label="Nome Fantasia" label-for="nome_fantasia">
                <b-form-input
                  id="nome_fantasia"
                  v-model="formData.nome_fantasia"
                  placeholder="Nome Fantasia"
                />
              </b-form-group>

              <b-form-group
                label="Data de Fundação"
                label-for="data_fundacao"
              >
                <b-form-input
                  id="data_fundacao"
                  v-model="formData.data_fundacao"
                  type="date"
                />
              </b-form-group>
            </template>

            <template v-else>
              <b-form-group label="RG" label-for="rg">
                <validation-provider
                  #default="{ errors }"
                  name="RG"
                  rules="required"
                >
                  <b-form-input
                    id="rg"
                    v-model="formData.rg"
                    v-mask="'##.###.###-#'"
                    placeholder="Digite seu RG"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="Órgão Expedidor" label-for="orgao_expedidor">
                <validation-provider
                  #default="{ errors }"
                  name="Órgão Expedidor"
                  rules="required"
                >
                  <b-form-input
                    id="orgao_expedidor"
                    v-model="formData.orgao_expedidor"
                    placeholder="Ex: SSP/SP"
                    style="text-transform: uppercase;"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="Data de Nascimento" label-for="data_nascimento">
                <b-form-input
                  id="data_nascimento"
                  v-model="formData.data_nascimento"
                  type="date"
                />
              </b-form-group>

              <b-form-group label="Sexo" label-for="sexo">
                <validation-provider
                  #default="{ errors }"
                  name="Sexo"
                  rules="required"
                >
                  <b-form-select
                    id="sexo"
                    v-model="formData.sexo"
                    :options="[
                      { value: 'M', text: 'Masculino' },
                      { value: 'F', text: 'Feminino' },
                      { value: 'O', text: 'Outro' },
                    ]"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </template>
          </div>

          <!-- Etapa 3: Dados do Responsável -->
          <div v-show="currentStep === 3">
            <b-form-group label="Nome" label-for="nome_responsavel">
              <validation-provider
                #default="{ errors }"
                name="Nome"
                rules="required"
              >
                <b-form-input
                  id="nome_responsavel"
                  v-model="formData.nome_responsavel"
                  placeholder="Nome do Responsável"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <b-form-group label="Sobrenome" label-for="sobrenome">
              <validation-provider
                #default="{ errors }"
                name="Sobrenome"
                rules="required"
              >
                <b-form-input
                  id="sobrenome"
                  v-model="formData.sobrenome"
                  placeholder="Sobrenome"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <b-form-group label="Celular" label-for="celular">
              <validation-provider
                #default="{ errors }"
                name="Celular"
                rules="required"
              >
                <b-form-input
                  id="celular"
                  v-model="formData.celular"
                  v-mask="'(##) #####-####'"
                  placeholder="(00) 00000-0000"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
            <b-form-group label="CPF" label-for="cpf">
              <validation-provider
                #default="{ errors }"
                name="CPF"
                rules="required"
              >
                <b-form-input
                  id="cpf"
                  v-model="formData.cpf"
                  v-mask="'###.###.###-##'"
                  placeholder="000.000.000-00"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </div>

          <div v-show="currentStep === 4">
            <b-form-group label="CEP" label-for="cep">
              <validation-provider
                #default="{ errors }"
                name="CEP"
                rules="required"
              >
                <b-form-input
                  id="cep"
                  v-model="formData.cep"
                  v-mask="'#####-###'"
                  placeholder="00000-000"
                  @change="buscarCep"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <b-form-group label="Rua" label-for="rua">
              <validation-provider
                #default="{ errors }"
                name="Rua"
                rules="required"
              >
                <b-form-input
                  id="rua"
                  v-model="formData.rua"
                  placeholder="Nome da Rua"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <b-row>
              <b-col md="4">
                <b-form-group label="Número" label-for="numero">
                  <validation-provider
                    #default="{ errors }"
                    name="Número"
                    rules="required"
                  >
                    <b-form-input
                      id="numero"
                      v-model="formData.numero"
                      placeholder="Nº"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <b-col md="8">
                <b-form-group
                  label="Complemento"
                  label-for="complemento"
                >
                  <b-form-input
                    id="complemento"
                    v-model="formData.complemento"
                    placeholder="Apto, Sala, Conjunto, etc."
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-form-group label="Bairro" label-for="bairro">
              <validation-provider
                #default="{ errors }"
                name="Bairro"
                rules="required"
              >
                <b-form-input
                  id="bairro"
                  v-model="formData.bairro"
                  placeholder="Bairro"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <b-row>
              <b-col md="8">
                <b-form-group label="Cidade" label-for="cidade">
                  <validation-provider
                    #default="{ errors }"
                    name="Cidade"
                    rules="required"
                  >
                    <b-form-input
                      id="cidade"
                      v-model="formData.cidade"
                      placeholder="Cidade"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <b-col md="4">
                <b-form-group label="Estado" label-for="estado">
                  <validation-provider
                    #default="{ errors }"
                    name="Estado"
                    rules="required"
                  >
                    <b-form-select
                      id="estado"
                      v-model="formData.estado"
                      :options="estadoOptions"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
          </div>

          <!-- Etapa 5: Dados Bancários -->
          <div v-show="currentStep === 5">
            <b-form-group label="Banco" label-for="banco">
              <validation-provider
                #default="{ errors }"
                name="Banco"
                rules="required"
              >
                <b-form-select
                  id="banco"
                  v-model="formData.banco"
                  :options="bancoOptions"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <b-form-group label="Agência" label-for="agencia">
              <validation-provider
                #default="{ errors }"
                name="Agência"
                rules="required"
              >
                <b-row>
                  <b-col cols="8">
                    <b-form-input
                      id="agencia"
                      v-model="formData.agencia"
                      placeholder="Número da Agência"
                      :state="errors.length > 0 ? false : null"
                    />
                  </b-col>
                  <b-col cols="4">
                    <b-form-input
                      id="digito_agencia"
                      v-model="formData.digito_agencia"
                      placeholder="Dígito"
                      maxlength="1"
                    />
                  </b-col>
                </b-row>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <b-form-group label="Conta" label-for="conta">
              <validation-provider
                #default="{ errors }"
                name="Conta"
                rules="required"
              >
                <b-row>
                  <b-col cols="8">
                    <b-form-input
                      id="conta"
                      v-model="formData.conta"
                      placeholder="Número da Conta"
                      :state="errors.length > 0 ? false : null"
                    />
                  </b-col>
                  <b-col cols="4">
                    <b-form-input
                      id="digito_conta"
                      v-model="formData.digito_conta"
                      placeholder="Dígito"
                      maxlength="1"
                    />
                  </b-col>
                </b-row>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </div>

          <!-- Botões de Navegação -->
          <div class="d-flex justify-content-between mt-2">
            <b-button
              v-if="currentStep > 1"
              variant="outline-primary"
              @click="previousStep"
            >
              <feather-icon
                icon="ChevronLeftIcon"
                class="mr-1"
              />
              Anterior
            </b-button>
            <b-button
              v-if="currentStep < steps.length"
              variant="primary"
              @click="nextStep"
            >
              Próximo
              <feather-icon
                icon="ChevronRightIcon"
                class="ml-1"
              />
            </b-button>
            <b-button
              v-if="currentStep === steps.length"
              variant="success"
              @click="submitForm"
            >
              <feather-icon
                icon="CheckIcon"
                class="mr-1"
              />
              Finalizar Cadastro
            </b-button>
          </div>
        </validation-observer>

        <!-- Link para Login -->
        <b-card-text class="text-center mt-2">
          <span>Já possui uma conta? </span>
          <b-link :to="{name:'login'}">
            <span>Faça login</span>
          </b-link>
        </b-card-text>
      </b-card>
    </div>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import {
  BButton,
  BCard,
  BCardText,
  BCardTitle,
  BForm,
  BFormCheckbox,
  BFormGroup,
  BFormSelect,
  BFormInput,
  BFormRadioGroup,
  BInputGroup,
  BInputGroupAppend,
  BLink,
  VBTooltip,
  BRow,
  BCol,
} from 'bootstrap-vue'
import VuexyLogo from '@core/layouts/components/Logo.vue'
import { email, required } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import { mask } from 'vue-the-mask'
import useJwt from '@/auth/jwt/useJwt'
import axios from '@axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    VuexyLogo,
    BCard,
    BLink,
    BCardTitle,
    BCardText,
    BFormSelect,
    BForm,
    BFormRadioGroup,
    BButton,
    BFormInput,
    BFormGroup,
    BInputGroup,
    BInputGroupAppend,
    BFormCheckbox,
    BRow,
    BCol,
    ValidationProvider,
    ValidationObserver,
  },

  directives: {
    mask,
  },
  mixins: [togglePasswordVisibility],

  data() {
    return {
      currentStep: 1,
      steps: [
        { title: 'Login' },
        { title: 'Pessoais' },
        { title: 'Resp.' },
        { title: 'Endereço' },
        { title: 'Bancários' },
      ],
      formData: {
        login: '',
        senha: '',
        nome_cliente: '',
        codigo: '',
        aprovado: 'Pendente',
        id_grupousuario: null,
        tipo_pessoa: 'F',
        cnpj: '',
        razao_social: '',
        nome_fantasia: '',
        contribuinte_ie: false,
        inscricao_estadual: '',
        nome_responsavel: '',
        sobrenome: '',
        celular: '',
        cpf: '',
        data_fundacao: null,
        rg: '',
        orgao_expedidor: '',
        data_nascimento: null,
        sexo: '',
        cep: '',
        rua: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        banco: '',
        conta: '',
        digito_conta: '',
        agencia: '',
        digito_agencia: '',
        codigo_banco: '',
      },

      estadoOptions: [
        { value: null, text: 'Selecione o estado' },
        { value: 'AC', text: 'AC' },
        { value: 'AL', text: 'AL' },
        { value: 'AP', text: 'AP' },
        { value: 'AM', text: 'AM' },
        { value: 'BA', text: 'BA' },
        { value: 'CE', text: 'CE' },
        { value: 'DF', text: 'DF' },
        { value: 'ES', text: 'ES' },
        { value: 'GO', text: 'GO' },
        { value: 'MA', text: 'MA' },
        { value: 'MT', text: 'MT' },
        { value: 'MS', text: 'MS' },
        { value: 'MG', text: 'MG' },
        { value: 'PA', text: 'PA' },
        { value: 'PB', text: 'PB' },
        { value: 'PR', text: 'PR' },
        { value: 'PE', text: 'PE' },
        { value: 'PI', text: 'PI' },
        { value: 'RJ', text: 'RJ' },
        { value: 'RN', text: 'RN' },
        { value: 'RS', text: 'RS' },
        { value: 'RO', text: 'RRO' },
        { value: 'RR', text: 'RR' },
        { value: 'SC', text: 'SC' },
        { value: 'SP', text: 'SP' },
        { value: 'SE', text: 'SE' },
        { value: 'TO', text: 'TO' },
      ],

      groupOptions: [
        { value: 4, text: 'Afiliado' },
        { value: 5, text: 'Vendedor' },
        { value: 6, text: 'Padrinho' },
      ],

      bancoOptions: [
        { value: '001', text: 'Banco do Brasil' },
        { value: '003', text: 'Banco da Amazônia' },
        { value: '004', text: 'Banco do Nordeste' },
        { value: '007', text: 'BNDES' },
        { value: '010', text: 'Credicoamo' },
        { value: '011', text: 'Credit Suisse Hedging-Griffo' },
        { value: '021', text: 'Banestes' },
        { value: '025', text: 'Banco Alfa' },
        { value: '029', text: 'Banco Itaú Consignado' },
        { value: '033', text: 'Santander' },
        { value: '037', text: 'Banco do Estado do Pará (Banpará)' },
        { value: '040', text: 'Banco Cargill' },
        { value: '041', text: 'Banrisul' },
        { value: '047', text: 'Banco do Estado de Sergipe (Banese)' },
        { value: '070', text: 'Banco de Brasília (BRB)' },
        { value: '077', text: 'Banco Inter' },
        { value: '081', text: 'BancoSeguro' },
        { value: '104', text: 'Caixa Econômica Federal' },
        { value: '136', text: 'Unicred' },
        { value: '237', text: 'Bradesco' },
        { value: '260', text: 'Nu Pagamentos (Nubank)' },
        { value: '290', text: 'PagBank' },
        { value: '323', text: 'Mercado Pago' },
        { value: '341', text: 'Itaú Unibanco' },
        { value: '380', text: 'PicPay' },
        { value: '403', text: 'C6 Bank' },
        { value: '412', text: 'Banco Capital' },
        { value: '423', text: 'Banco Ribeirão Preto' },
        { value: '456', text: 'Banco MUFG Brasil' },
        { value: '464', text: 'Banco Sumitomo Mitsui' },
        { value: '473', text: 'Banco Caixa Geral Brasil' },
        { value: '477', text: 'Citibank' },
        { value: '479', text: 'Banco Itaubank' },
        { value: '487', text: 'Deutsche Bank' },
        { value: '488', text: 'JPMorgan Chase Bank' },
        { value: '492', text: 'Banco NMB Postbank' },
        { value: '494', text: 'Banco la República Oriental del Uruguay' },
        { value: '495', text: 'Banco de La Provincia de Buenos Aires' },
        { value: '505', text: 'Banco Credit Agricole Brasil' },
        { value: '545', text: 'Senso' },
        { value: '623', text: 'Banco Pan' },
        { value: '626', text: 'Banco Ficsa' },
        { value: '630', text: 'Banco Smartbank' },
        { value: '633', text: 'Banco Rendimento' },
        { value: '634', text: 'Banco Triângulo (Banco Tri)' },
        { value: '637', text: 'Banco Sofisa' },
        { value: '643', text: 'Banco Pine' },
        { value: '652', text: 'Itaú Unibanco Holding' },
        { value: '653', text: 'Banco Indusval' },
        { value: '654', text: 'Banco A. J. Renner' },
        { value: '707', text: 'Banco Daycoval' },
        { value: '712', text: 'Banco Ourinvest' },
        { value: '745', text: 'Banco Citibank' },
        { value: '746', text: 'Banco Modal' },
        { value: '747', text: 'Banco Rabobank Internacional' },
        { value: '748', text: 'Sicredi' },
        { value: '751', text: 'Banco UBS Brasil' },
        { value: '752', text: 'BNP Paribas Brasil' },
        { value: '753', text: 'Novo Banco Continental S.A.' },
        { value: '754', text: 'Banco Sistema' },
        { value: '755', text: 'Bank of America Merrill Lynch' },
        { value: '756', text: 'Bancoob (Sicoob)' },
      ],
      required,
      email,
      isCodeReadOnly: false,
      isGroupReadOnly: false,
    }
  },

  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
  },

  mounted() {
    this.checkCodeFromUrl()
    this.checkGroupFromUrl()
  },

  methods: {
    checkCodeFromUrl() {
      const urlParams = new URLSearchParams(window.location.search)
      const codeFromUrl = urlParams.get('code')
      if (codeFromUrl) {
        this.formData.codigo = codeFromUrl
        this.isCodeReadOnly = true
      }
    },

    checkGroupFromUrl() {
      const urlParams = new URLSearchParams(window.location.search)
      const groupFromUrl = urlParams.get('group')
      if (groupFromUrl) {
        const groupId = parseInt(groupFromUrl)
        if (groupId === 4 || groupId === 5 || groupId === 6) {
          this.formData.id_grupousuario = groupId
          this.isGroupReadOnly = true
        }
      }
    },

    async buscarCep() {
      if (this.formData.cep.length === 9) {
        try {
          const cep = this.formData.cep.replace(/\D/g, '')
          const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
          const data = await response.json()
          if (!data.erro) {
            this.formData.rua = data.logradouro
            this.formData.bairro = data.bairro
            this.formData.cidade = data.localidade
            this.formData.estado = data.uf
          }
        } catch (error) {
          console.error('Erro ao buscar CEP:', error)
        }
      }
    },

    nextStep() {
      if (this.currentStep < this.steps.length) {
        this.currentStep++
      }
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--
      }
    },

    async submitForm() {
      try {
        const isValid = await this.$refs.registerForm.validate()
        if (isValid) {
          await axios.post('/auth/register', this.formData)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Registro realizado com sucesso!',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.$router.push({ name: 'login' })
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Atenção',
              text: 'Por favor, verifique se todos os campos obrigatórios foram preenchidos',
              icon: 'AlertCircleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao registrar',
            text: error.response?.data?.error || 'Ocorreu um erro',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/pages/page-auth.scss';

.bs-stepper-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 1rem 0;
  padding: 1rem;
  background: #f8f8f8;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .step {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    text-align: center;

    &.active {
      .bs-stepper-circle {
        background-color: var(--primary);
        color: white;
        transform: scale(1.1);
      }

      .bs-stepper-label {
        color: var(--primary);
        font-weight: 600;
      }
    }

    &.completed {
      .bs-stepper-circle {
        background-color: var(--success);
        color: white;
      }

      .line {
        background-color: var(--success);
      }
    }
  }

  .step-trigger {
    display: flex;
    flex-direction: column;
    align-items: center;
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
    width: 100%;
  }

  .bs-stepper-circle {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #eee;
    border-radius: 50%;
    margin-bottom: 0.5rem;
    transition: all 0.3s ease;
    font-size: 1rem;
    font-weight: bold;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    .feather {
      width: 18px;
      height: 18px;
    }
  }

  .bs-stepper-label {
    font-size: 0.875rem;
    color: #6e6b7b;
    text-align: center;
    margin-top: 0.25rem;
    white-space: normal; // Permite quebra de linha
    word-wrap: break-word; // Garante que palavras longas sejam quebradas
    max-width: 80px; // Define um limite de largura para o texto
  }

  .line {
    position: absolute;
    top: 20px; // Centraliza a linha com o círculo
    left: calc(50% + 20px);
    right: calc(-50% + 20px);
    height: 2px;
    background-color: #eee;
    z-index: 0;
  }

  .step:last-child .line {
    display: none;
  }
}
</style>
